import { ORDER_STATUS_LABELS, PILE_FOLDING_ALLOWANCE_CM } from '../constants';

// Date formatting - Firestore Timestamp ve Date nesnelerini destekler
export const formatDate = (dateValue) => {
  if (!dateValue) return '-';

  let date;

  // Firestore Timestamp nesnesi mi kontrol et
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('tr-TR');
};

// Tarih değerini input[type="date"] için YYYY-MM-DD formatına çevirir
export const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';

  let date;

  // Firestore Timestamp nesnesi mi kontrol et
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) {
    return '';
  }

  // YYYY-MM-DD formatına çevir
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Price formatting - Türk para birimi formatı
export const formatPrice = (price) => {
  const num = parseFloat(price || 0);

  // Türk para birimi formatı: 1.234,56
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Validation functions
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[0-9\s\-+()]{10,}$/;
  return phoneRegex.test(phone);
};

// Local storage helpers
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Local storage error:', error);
  }
};

export const getLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Local storage error:', error);
    return null;
  }
};

// Error handling
export const handleFirebaseError = (error) => {
  console.error('Firebase error:', error);

  switch (error.code) {
    case 'auth/user-not-found':
      return 'Bu email adresi ile kayıtlı kullanıcı bulunamadı.';
    case 'auth/wrong-password':
      return 'Şifre hatalı.';
    case 'auth/invalid-email':
      return 'Geçersiz email adresi.';
    case 'auth/too-many-requests':
      return 'Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin.';
    case 'permission-denied':
      return 'Firebase erişim izni yok. Lütfen Firebase güvenlik kurallarını kontrol edin.';
    case 'unavailable':
      return 'Firebase servisi şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.';
    default:
      return 'Bir hata oluştu. Lütfen tekrar deneyin.';
  }
};

// Loading states
export const createLoadingState = () => ({
  isLoading: false,
  error: null,
  data: null
});

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Tül Hesaplama Fonksiyonları
export const calculatePileDensity = (pileModel) => {
  const densityValues = {
    'Standart SEYREK': 2,
    'Standart ORTA': 2.5,
    'Standart SIK': 3,
    'S Pile': 3,
    'Amerikan Pile 2\'li': 3,
    'Amerikan Pile 3\'lü': 4,
    'Kapsül Pile': 3
  };

  return densityValues[pileModel] || 0;
};

const PILE_EXTRA_FIELD_MAP = {
  'S Pile': 'SPilePrice',
  "Amerikan Pile 2'li": 'American2Price',
  "Amerikan Pile 3'lü": 'American3Price'
};

const LEGACY_PILE_EXTRA_FIELD_MAP = {
  'S Pile': 'sPilePrice',
  "Amerikan Pile 2'li": 'american2Price',
  "Amerikan Pile 3'lü": 'american3Price'
};

export const getPileExtraPrice = (adminSettings, productType, pileModel) => {
  if (!adminSettings || !pileModel) return 0;

  const prefix = productType === 'tul' ? 'tul' : 'kanat';
  const fieldSuffix = PILE_EXTRA_FIELD_MAP[pileModel];
  if (fieldSuffix) {
    const productField = `${prefix}${fieldSuffix}`;
    if (adminSettings[productField] !== undefined && adminSettings[productField] !== '') {
      return Number(adminSettings[productField]) || 0;
    }
  }

  const legacyField = LEGACY_PILE_EXTRA_FIELD_MAP[pileModel];
  return legacyField ? (Number(adminSettings[legacyField]) || 0) : 0;
};

export const getKapsulUnitPrice = (adminSettings, productType) => {
  if (!adminSettings) return 0;
  return productType === 'tul'
    ? (Number(adminSettings.tulKapsulPrice) || 0)
    : (Number(adminSettings.kanatKapsulPrice) || 0);
};

export const getTulAltEtekKatlamaPrice = (adminSettings) => {
  if (!adminSettings) return 0;
  return Number(adminSettings.tulAltEtekKatlamaPrice) || 0;
};

export const parseTulExtraPricesFromSettings = (data = {}) => ({
  sPile: data.tulSPilePrice ?? data.sPilePrice ?? '',
  american2: data.tulAmerican2Price ?? data.american2Price ?? '',
  american3: data.tulAmerican3Price ?? data.american3Price ?? '',
  kapsul: data.tulKapsulPrice !== undefined ? data.tulKapsulPrice : '',
  altEtekKatlama: data.tulAltEtekKatlamaPrice !== undefined ? data.tulAltEtekKatlamaPrice : ''
});

export const parseKanatExtraPricesFromSettings = (data = {}) => ({
  sPile: data.kanatSPilePrice ?? data.sPilePrice ?? '',
  american2: data.kanatAmerican2Price ?? data.american2Price ?? '',
  american3: data.kanatAmerican3Price ?? data.american3Price ?? '',
  kapsul: data.kanatKapsulPrice !== undefined ? data.kanatKapsulPrice : ''
});

export const buildTulPileSettingsPayload = (extraPrices) => ({
  tulSPilePrice: Number(extraPrices.sPile),
  tulAmerican2Price: Number(extraPrices.american2),
  tulAmerican3Price: Number(extraPrices.american3),
  tulKapsulPrice: Number(extraPrices.kapsul) || 0,
  tulAltEtekKatlamaPrice: Number(extraPrices.altEtekKatlama) || 0
});

export const buildKanatPileSettingsPayload = (extraPrices) => ({
  kanatSPilePrice: Number(extraPrices.sPile),
  kanatAmerican2Price: Number(extraPrices.american2),
  kanatAmerican3Price: Number(extraPrices.american3),
  kanatKapsulPrice: Number(extraPrices.kapsul) || 0
});

export const parseAdminPileSettings = (data = {}) => ({
  tulSPilePrice: Number(data.tulSPilePrice ?? data.sPilePrice) || 0,
  tulAmerican2Price: Number(data.tulAmerican2Price ?? data.american2Price) || 0,
  tulAmerican3Price: Number(data.tulAmerican3Price ?? data.american3Price) || 0,
  kanatSPilePrice: Number(data.kanatSPilePrice ?? data.sPilePrice) || 0,
  kanatAmerican2Price: Number(data.kanatAmerican2Price ?? data.american2Price) || 0,
  kanatAmerican3Price: Number(data.kanatAmerican3Price ?? data.american3Price) || 0,
  tulKapsulPrice: Number(data.tulKapsulPrice) || 0,
  tulAltEtekKatlamaPrice: Number(data.tulAltEtekKatlamaPrice) || 0,
  kanatKapsulPrice: Number(data.kanatKapsulPrice) || 0,
  sPilePrice: Number(data.sPilePrice) || 0,
  american2Price: Number(data.american2Price) || 0,
  american3Price: Number(data.american3Price) || 0
});

export const getStorZebraExtraPrice = (adminSettings, productType, extraKey) => {
  if (!adminSettings || !extraKey) return 0;

  const sharedKeys = {
    jumbo: { stor: 'storJumboPrice', zebra: 'zebraJumboPrice' },
    altEtekDilim: { stor: 'storAltEtekDilimPrice', zebra: 'zebraAltEtekDilimPrice' },
    kapaliKasa: { stor: 'storKapaliKasaPrice', zebra: 'zebraKapaliKasaPrice' }
  };

  const storOnlyKeys = {
    altEtekLazer: 'storAltEtekLazerPrice',
    dekoratifLama: 'storDekoratifLamaPrice',
    dekoratifKasa: 'storDekoratifKasaPrice',
    altEtekBeyazIs: 'storAltEtekBeyazIsPrice',
    boru47: 'storBoru47Price',
    mekanizma47: 'storMekanizma47Price'
  };

  const shared = sharedKeys[extraKey];
  if (shared) {
    const field = productType === 'zebra' ? shared.zebra : shared.stor;
    return Number(adminSettings[field]) || 0;
  }

  if (productType === 'stor' && storOnlyKeys[extraKey]) {
    return Number(adminSettings[storOnlyKeys[extraKey]]) || 0;
  }

  return 0;
};

export const parseStorExtrasFromSettings = (data = {}) => ({
  jumbo: data.storJumboPrice || '',
  altEtekDilim: data.storAltEtekDilimPrice || '',
  kapaliKasa: data.storKapaliKasaPrice || '',
  altEtekLazer: data.storAltEtekLazerPrice || '',
  dekoratifLama: data.storDekoratifLamaPrice || '',
  dekoratifKasa: data.storDekoratifKasaPrice || '',
  altEtekBeyazIs: data.storAltEtekBeyazIsPrice || '',
  boru47: data.storBoru47Price || '',
  mekanizma47: data.storMekanizma47Price || '',
  motorCurrency: data.storMotorCurrency || 'USD',
  motorPrice: data.storMotorPrice || ''
});

export const parseZebraExtrasFromSettings = (data = {}) => ({
  jumbo: data.zebraJumboPrice || '',
  altEtekDilim: data.zebraAltEtekDilimPrice || '',
  kapaliKasa: data.zebraKapaliKasaPrice || '',
  motorCurrency: data.zebraMotorCurrency || 'USD',
  motorPrice: data.zebraMotorPrice || ''
});

export const buildStorSettingsPayload = (extras) => ({
  storJumboPrice: Number(extras.jumbo),
  storAltEtekDilimPrice: Number(extras.altEtekDilim),
  storKapaliKasaPrice: Number(extras.kapaliKasa),
  storAltEtekLazerPrice: Number(extras.altEtekLazer),
  storDekoratifLamaPrice: Number(extras.dekoratifLama),
  storDekoratifKasaPrice: Number(extras.dekoratifKasa),
  storAltEtekBeyazIsPrice: Number(extras.altEtekBeyazIs),
  storBoru47Price: Number(extras.boru47),
  storMekanizma47Price: Number(extras.mekanizma47),
  storMotorCurrency: extras.motorCurrency,
  storMotorPrice: Number(extras.motorPrice) || ''
});

export const buildZebraSettingsPayload = (extras) => ({
  zebraJumboPrice: Number(extras.jumbo),
  zebraAltEtekDilimPrice: Number(extras.altEtekDilim),
  zebraKapaliKasaPrice: Number(extras.kapaliKasa),
  zebraMotorCurrency: extras.motorCurrency,
  zebraMotorPrice: Number(extras.motorPrice) || ''
});

export const calculateUsedM2 = (width, height, pileModel, customPileDensity = null) => {
  // Eğer custom pile sıklığı verilmişse onu kullan, yoksa pile modelinden hesapla
  let pileDensity = customPileDensity !== null ? parseFloat(customPileDensity) : calculatePileDensity(pileModel);

  // CM değerlerini metre'ye çevir (100'e böl)
  const widthNum = (parseFloat(width) || 0) / 100;
  const heightNum = (parseFloat(height) || 0) / 100;

  if (!pileDensity || pileDensity === 0) {
    // Pile sıklığı olmayan modeller için
    switch (pileModel) {
      case 'Çima Dikiş':
        return widthNum + 0.75;
      case 'Düz Dikiş':
        return widthNum + 0.5;
      case 'Kumaş Güneşlik':
        return widthNum + 0.5;
      default:
        return widthNum;
    }
  }

  // Pile sıklığı olan modeller için: en (m) x pile sıklığı + katlama payı
  return (widthNum * pileDensity) + (PILE_FOLDING_ALLOWANCE_CM / 100);
};

export const calculateCapsulePrice = (usedM2, capsuleUnitPrice) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const capsuleUnitPriceNum = parseFloat(capsuleUnitPrice) || 0;

  // Metrekareyi bir üst tam sayıya yuvarla
  const roundedM2 = Math.ceil(usedM2Num);

  // Metrekarede 7 kapsül kullanılıyor
  const capsuleCount = roundedM2 * 7;

  return capsuleCount * capsuleUnitPriceNum;
};

export const calculateTotalPrice = (usedM2, unitPrice, extraPrice, pileModel, capsulePrice = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const extraPriceNum = parseFloat(extraPrice) || 0;
  const capsulePriceNum = parseFloat(capsulePrice) || 0;

  // Standart modeller - ek ücret varsa ekle
  if (['Standart SEYREK', 'Standart ORTA', 'Standart SIK'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Dikiş modelleri - ek ücret varsa ekle
  if (['Çima Dikiş', 'Düz Dikiş', 'Kumaş Güneşlik'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Ek ücretli modeller
  if (['S Pile', 'Amerikan Pile 2\'li', 'Amerikan Pile 3\'lü'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Kapsül Pile - her zaman kapsül ücreti ekle (ek ücret boş olsa bile)
  if (pileModel === 'Kapsül Pile') {
    const basePrice = usedM2Num * (unitPriceNum + extraPriceNum);
    return basePrice + capsulePriceNum;
  }

  return 0;
};

export const calculateAstarPrice = (usedM2, astarUnitPrice, quantity = 1) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const astarUnitPriceNum = parseFloat(astarUnitPrice) || 0;
  const quantityNum = parseFloat(quantity) || 1;
  return usedM2Num * astarUnitPriceNum * quantityNum;
};

export const calculateOzelTasarimTotal = (order) => {
  if (!order?.hasOzelTasarim) return 0;

  const ozelDensity = parseFloat(order.ozelPileDensity) || 0;
  const ozelUsedM2Val = order.ozelUsedM2
    ? parseFloat(order.ozelUsedM2)
    : calculateUsedM2(order.ozelWidth, order.ozelHeight, order.ozelPileModel, ozelDensity > 0 ? ozelDensity : null);

  let capsulePrice = 0;
  if (order.ozelPileModel === 'Kapsül Pile' && order.ozelCapsuleUnitPrice) {
    capsulePrice = calculateCapsulePrice(order.ozelUsedM2 || order.ozelWidth, order.ozelCapsuleUnitPrice);
  }

  const ozelPricePerUnit = calculateTotalPrice(
    ozelUsedM2Val,
    order.ozelUnitPrice,
    order.ozelExtraPrice,
    order.ozelPileModel,
    capsulePrice
  );

  const ozelQty = parseFloat(order.ozelQuantity) || 1;
  return ozelPricePerUnit * ozelQty;
};

export const calculateTotalCost = (usedM2, unitCost) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  return usedM2Num * unitCostNum;
};

export const calculateDiscountedPrice = (totalPrice, discount) => {
  const totalPriceNum = parseFloat(totalPrice) || 0;
  const discountNum = parseFloat(discount) || 0;

  if (discountNum <= 0) return totalPriceNum;

  const discountAmount = totalPriceNum * (discountNum / 100);
  return totalPriceNum - discountAmount;
};

// Stor ve Zebra Hesaplama Fonksiyonları
export const getStorZebraCalcOptions = (order = {}, productType = 'stor') => ({
  productType,
  fireIptal: productType === 'stor' ? !!order.storFireIptal : false
});

export const isStorFireBumpZone = (widthCm) => {
  const width = parseFloat(widthCm) || 0;
  return width > 140 && width <= 200;
};

export const resolveStorWidthLimitCm = (order = {}, productItems = []) => {
  if (order.productItemId) {
    const byId = productItems.find((p) => p.id === order.productItemId);
    if (byId?.maxWidth200cm) return 200;
  }
  if (order.productMaxWidth200cm) return 200;
  if (order.productName) {
    const byName = productItems.find((p) => p.productType === 'stor' && p.name === order.productName);
    if (byName?.maxWidth200cm) return 200;
  }
  return null;
};

export const calculateStorZebraUsedM2 = (width, height, options = {}) => {
  const { productType = 'stor', fireIptal = false } = options;
  const widthCm = parseFloat(width) || 0;
  let widthNum = widthCm / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // En için minimum 1 metre kontrolü
  if (widthNum < 1) {
    widthNum = 1;
  }

  // Boy için minimum 2 metre; 2m üzeri bir üst ondalığa tamamlanır
  if (heightNum <= 2) {
    heightNum = 2;
  } else {
    heightNum = Math.ceil(heightNum * 10) / 10;
  }

  // Stor: 141–200 cm aralığında fire tamamlama (Fire İptal yoksa 2m)
  if (productType === 'stor' && !fireIptal && isStorFireBumpZone(widthCm)) {
    widthNum = 2;
  } else if (widthNum > 1) {
    widthNum = Math.ceil(widthNum * 10) / 10;
  }

  return widthNum * heightNum;
};

export const calculateStorZebraTotalPrice = (usedM2, unitPrice, width, extras = {}, motorPriceTL = 0, mounting = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  
  // Ekstra seçenekler için özel en hesaplaması
  let widthForExtras = (parseFloat(width) || 0) / 100; // cm -> metre
  if (widthForExtras > 0) {
    if (widthForExtras < 1) widthForExtras = 1;
    // 50cm (0.5m) katlarına yukarı yuvarla
    widthForExtras = Math.ceil(widthForExtras * 2) / 2;
  }

  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;
  const mountingNum = parseFloat(mounting) || 0;

  const basePrice = usedM2Num * unitPriceNum;
  
  // Ekstra ücretleri hesaplanan en (widthForExtras) ile çarpıyoruz
  const extraCostsTotal = Object.values(extras).reduce((sum, val) => {
    const valNum = parseFloat(val) || 0;
    return sum + (valNum * widthForExtras);
  }, 0);

  return basePrice + extraCostsTotal + motorPriceTLNum + mountingNum;
};

// Ahşap Jaluzi, Metal Jaluzi ve Dikey ürünler — Plise ile aynı m² mantığı
const roundDimensionToUpperTenthMeter = (valueCm) => {
  const meters = (parseFloat(valueCm) || 0) / 100;
  return Math.ceil(meters * 10) / 10;
};

export const calculateRoundedWidthHeightArea = (width, height) => {
  const widthNum = roundDimensionToUpperTenthMeter(width);
  const heightNum = roundDimensionToUpperTenthMeter(height);
  return widthNum * heightNum;
};

const calculateRoundedAreaWithMinOneM2 = (width, height) => {
  const calculatedArea = calculateRoundedWidthHeightArea(width, height);
  return calculatedArea >= 1 ? calculatedArea : 1;
};

export const calculateJaluziUsedM2 = (width, height) => calculateRoundedAreaWithMinOneM2(width, height);

export const calculateJaluziTotalPrice = (usedM2, unitPrice, mounting = 0, motorPriceTL = 0, zigilAmount = 0, ikiCamArasiAmount = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;
  const zigilAmountNum = parseFloat(zigilAmount) || 0;
  const ikiCamArasiAmountNum = parseFloat(ikiCamArasiAmount) || 0;

  const basePrice = usedM2Num * unitPriceNum;

  return basePrice + mountingNum + motorPriceTLNum + zigilAmountNum + ikiCamArasiAmountNum;
};

// Dikey Perde Hesaplama Fonksiyonu
export const calculateDikeyPerdeTotalPrice = (usedM2, unitPrice, backgroundCurtain = 0, mounting = 0, motorPriceTL = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const backgroundCurtainNum = parseFloat(backgroundCurtain) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;

  const basePrice = usedM2Num * (unitPriceNum + backgroundCurtainNum);

  return basePrice + mountingNum + motorPriceTLNum;
};

// Plise Hesaplama Fonksiyonu
export const calculatePliseUsedM2 = (width, height) => calculateRoundedAreaWithMinOneM2(width, height);

export const PLISE_MONTAJ_OPTIONS = [
  'Klipsli Montaj',
  'Portraylı Montaj',
  'Yapıştırma Sistem',
  'Isıcamlı Yapıştırma'
];

export const DEFAULT_PLISE_MONTAJ_OPTION = 'Klipsli Montaj';

export const createDefaultPliseItem = (id = Date.now()) => ({
  id,
  width: '',
  height: '',
  usedM2: '1.00',
  quantity: '',
  multisistem: false,
  montajOption: DEFAULT_PLISE_MONTAJ_OPTION,
  rowTotal: '0.00'
});

export const normalizePliseMontajSettings = (settings = {}) => ({
  plisePortrayliMontajPrice: Number(settings.plisePortrayliMontajPrice ?? settings.portrayliMontaj) || 0,
  pliseYapistirmaSistemPrice: Number(settings.pliseYapistirmaSistemPrice ?? settings.yapistirmaSistem) || 0,
  pliseIsicamliYapistirmaPrice: Number(settings.pliseIsicamliYapistirmaPrice ?? settings.isicamliYapistirma) || 0
});

export const parsePliseExtrasFromSettings = (data = {}) => {
  const normalized = normalizePliseMontajSettings(data);
  return {
    ...normalized,
    portrayliMontaj: normalized.plisePortrayliMontajPrice,
    yapistirmaSistem: normalized.pliseYapistirmaSistemPrice,
    isicamliYapistirma: normalized.pliseIsicamliYapistirmaPrice
  };
};

export const buildPliseSettingsPayload = (extras) => ({
  plisePortrayliMontajPrice: Number(extras.portrayliMontaj) || 0,
  pliseYapistirmaSistemPrice: Number(extras.yapistirmaSistem) || 0,
  pliseIsicamliYapistirmaPrice: Number(extras.isicamliYapistirma) || 0
});

export const getPliseMontajExtraPrice = (settings = {}, montajOption) => {
  if (!montajOption || montajOption === DEFAULT_PLISE_MONTAJ_OPTION) return 0;

  const normalized = normalizePliseMontajSettings(settings);
  const priceMap = {
    'Portraylı Montaj': normalized.plisePortrayliMontajPrice,
    'Yapıştırma Sistem': normalized.pliseYapistirmaSistemPrice,
    'Isıcamlı Yapıştırma': normalized.pliseIsicamliYapistirmaPrice
  };

  return Number(priceMap[montajOption]) || 0;
};

export const calculatePliseTotalPrice = (usedM2, unitPrice1 = 0, unitPrice2 = 0, mounting = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPrice1Num = parseFloat(unitPrice1) || 0;
  const unitPrice2Num = parseFloat(unitPrice2) || 0;
  const mountingNum = parseFloat(mounting) || 0;

  // Toplam birim fiyat (iki ürünün toplamı)
  const totalUnitPrice = unitPrice1Num + unitPrice2Num;

  // Eğer m2 0 ise (ölçü girilmemişse) varsayılan 1 m2 üzerinden hesapla 
  // (Ancak birim fiyatlar 0 ise sonuç yine 0/montaj olacaktır)
  const effectiveM2 = usedM2Num > 0 ? usedM2Num : 1;

  const basePrice = effectiveM2 * totalUnitPrice;

  return basePrice + mountingNum;
};

// Aksesuar Hesaplama Fonksiyonu
export const calculateAksesuarTotalPrice = (unitPrice, quantity) => {
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const quantityNum = parseFloat(quantity) || 0;

  return unitPriceNum * quantityNum;
};

// Katlamalı Perde Hesaplama Fonksiyonları
export const calculateKatlamaliPerdeUsedM = (width) => {
  // CM değerini metre'ye çevir ve 0.35 ekle
  const widthNum = (parseFloat(width) || 0) / 100;
  return widthNum + 0.35;
};

export const calculateKatlamaliPerdeDikimPrice = (width, height, dikimUnitPrice = 0) => {
  const area = calculateRoundedWidthHeightArea(width, height);
  const unitPrice = parseFloat(dikimUnitPrice) || 0;
  return area * unitPrice;
};

export const parseKatlamaliExtrasFromSettings = (data = {}) => ({
  mekanizmaList: data.katlamaliMekanizmaList || [],
  motorCurrency: data.katlamaliPerdeMotorCurrency || 'USD',
  motorPrice: data.katlamaliPerdeMotorPrice || '',
  dikimUcreti: data.katlamaliPerdeDikimPrice ?? '',
  katlamaliPerdeDikimPrice: Number(data.katlamaliPerdeDikimPrice) || 0
});

export const buildKatlamaliSettingsPayload = (extras) => ({
  katlamaliMekanizmaList: extras.mekanizmaList || [],
  katlamaliPerdeMotorCurrency: extras.motorCurrency || 'USD',
  katlamaliPerdeMotorPrice: Number(extras.motorPrice) || 0,
  katlamaliPerdeDikimPrice: Number(extras.dikimUcreti ?? extras.katlamaliPerdeDikimPrice) || 0
});

export const calculateKatlamaliPerdeTotalPrice = (
  usedM,
  fabricPrice = 0,
  mechanismPrice = 0,
  accessories = [],
  mounting = 0,
  motorPrice = 0,
  quantity = 1,
  dikimPrice = 0
) => {
  const usedMNum = parseFloat(usedM) || 0;
  const fabricPriceNum = parseFloat(fabricPrice) || 0;
  const mechanismPriceNum = parseFloat(mechanismPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceNum = parseFloat(motorPrice) || 0;
  const dikimPriceNum = parseFloat(dikimPrice) || 0;
  const qtyNum = parseFloat(quantity) || 1;

  // Aksesuar toplamını hesapla
  let totalAccessoryPrice = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryPrice = accessories.reduce((sum, acc) => {
      const p = parseFloat(acc.price) || 0;
      const q = parseFloat(acc.quantity) || 0;
      return sum + (p * q);
    }, 0);
  }

  // Toplam Tutar = ((Kullanılan metre x (Kumaş + Mekanizma)) + Aksesuarlar + Montaj + Motor + Dikim) x Adet
  const basePrice = usedMNum * (fabricPriceNum + mechanismPriceNum);
  return (basePrice + totalAccessoryPrice + mountingNum + motorPriceNum + dikimPriceNum) * qtyNum;
};

export const calculateRustikUsedCm = (width, options = {}) => {
  let widthNum = parseFloat(width) || 0;
  if (widthNum <= 0) return 0;

  const minCm = parseInt(options.rustikMinCm, 10) || 120;
  if (widthNum <= minCm) return minCm;

  const mode = options.rustikOlcuTamamlama || DEFAULT_RUSTIK_OLCU_TAMAMLAMA;

  switch (mode) {
    case '25cm':
      return Math.ceil(widthNum / 25) * 25;
    case '50cm':
      return Math.ceil(widthNum / 50) * 50;
    case 'urunBoyutu':
      return Math.ceil(widthNum / 10) * 10;
    case 'standart':
    default:
      return Math.ceil(widthNum / 40) * 40;
  }
};

export const RUSTIK_MIN_CM_OPTIONS = [
  { value: '120', label: '120 cm' },
  { value: '100', label: '100 cm' }
];

export const DEFAULT_RUSTIK_MIN_CM = '120';

export const RUSTIK_OLCU_TAMAMLAMA_OPTIONS = [
  { value: 'standart', label: "Standart Uygulama (40'a tamamla)" },
  { value: '25cm', label: "25 cm'e tamamla" },
  { value: '50cm', label: "50 cm'e tamamla" },
  { value: 'urunBoyutu', label: 'Ürün boyutu' }
];

export const DEFAULT_RUSTIK_OLCU_TAMAMLAMA = 'standart';

// Ray Sistemi Hesaplama Fonksiyonları
export const calculateRaySistemiUsedMeter = (width, olcuTamamlama) => {
  let widthNum = (parseFloat(width) || 0) / 100; // cm -> m
  if (widthNum <= 0) return 0;

  if (olcuTamamlama === '25cm') {
    // 25cm (0.25m) katlarına yukarı yuvarla
    widthNum = Math.ceil(widthNum * 4) / 4;
  } else if (olcuTamamlama === '50cm') {
    // 50cm (0.5m) katlarına yukarı yuvarla
    widthNum = Math.ceil(widthNum * 2) / 2;
  }
  // urunBoyutu seçeneğinde yuvarlama yapma

  return widthNum;
};

export const calculateRustikTotalPrice = (unitPrice, quantity, ayakAdeti = 0, ekAyakSayisi = 0, ayakUcreti = 0, mounting = 0, rayOptions = [], accessories = [], usedM2 = 0) => {
  const upNum = parseFloat(unitPrice) || 0;
  const qtyNum = parseFloat(quantity) || 1;
  const ekAAdet = parseFloat(ekAyakSayisi) || 0;
  const aUcret = parseFloat(ayakUcreti) || 0;
  const mnt = parseFloat(mounting) || 0;
  const usedCm = parseFloat(usedM2) || 0;
  const usedMeters = usedCm / 100;

  // Birim fiyat direkt adetle çarpılır
  const basePrice = upNum * qtyNum;

  // Sadece ek ayaklar ücretlendirilir
  const ayakToplam = ekAAdet * aUcret;

  // Ray seçenekleri toplamı: Ray Fiyatı * Kullanılan Metre
  let totalRayPrice = 0;
  if (Array.isArray(rayOptions)) {
    totalRayPrice = rayOptions.reduce((sum, opt) => {
      const p = parseFloat(opt.price) || 0;
      return sum + (p * usedMeters);
    }, 0);
  }

  // Aksesuar toplamı: Fiyat * Adet
  let totalAccessoryPrice = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryPrice = accessories.reduce((sum, acc) => {
      const p = parseFloat(acc.price) || 0;
      const q = parseFloat(acc.quantity) || 0;
      return sum + (p * q);
    }, 0);
  }

  // Toplam Tutar = (Birim Fiyat x Adet) + (Ek Ayak x Ayak Ücreti) + Ray Seçenekleri + Aksesuarlar + Montaj
  return basePrice + ayakToplam + totalRayPrice + totalAccessoryPrice + mnt;
};

// Dashboard İstatistik Hesaplama Fonksiyonları
export const calculateOrderStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: orders.length,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  };

  orders.forEach(order => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = order.orderDate || order.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);

    if (createdAt >= today) {
      stats.today++;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek++;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth++;
    }
  });

  return stats;
};

export const calculateRevenueStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    average: 0
  };

  orders.forEach(order => {
    // İndirimli tutar varsa onu kullan, yoksa toplam tutarı kullan
    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    stats.total += orderAmount;

    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);

    if (createdAt >= today) {
      stats.today += orderAmount;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek += orderAmount;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth += orderAmount;
    }
  });

  stats.average = orders.length > 0 ? stats.total / orders.length : 0;

  return stats;
};

export const calculateProductTypeDistribution = (orders) => {
  const distribution = {};

  orders.forEach(order => {
    const productType = order.productType || 'unknown';
    distribution[productType] = (distribution[productType] || 0) + 1;
  });

  return distribution;
};

export const calculateOrderStatusDistribution = (customers) => {
  const distribution = {};

  // ORDER_STATUS_LABELS'dan tüm durumları al
  Object.values(ORDER_STATUS_LABELS).forEach(label => {
    distribution[label] = 0;
  });

  customers.forEach(customer => {
    const status = customer.orderStatus || 'new_order';
    const statusLabel = ORDER_STATUS_LABELS[status] || 'Yeni Sipariş';
    distribution[statusLabel] = (distribution[statusLabel] || 0) + 1;
  });

  return distribution;
};

export const calculateMonthlyCustomerGrowth = (customers) => {
  const monthlyData = {};

  customers.forEach(customer => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = customer.orderDate || customer.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
  });

  return monthlyData;
};

export const calculateMonthlyRevenue = (orders) => {
  const monthlyData = {};

  orders.forEach(order => {
    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + orderAmount;
  });

  return monthlyData;
}; 